/**
 * Public Flyer Viewer — no-auth, at /flyer/:slug
 * Renders the saved flyer using the same FlyerCanvas + provides download/share/save-contact.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function FlyerPublicView() {
  const { slug } = useParams();
  const [flyer, setFlyer] = useState(null);
  const [err, setErr] = useState(null);
  const previewRef = useRef(null);

  useEffect(() => {
    axios.get(`${BASE}/api/flyer/public/${slug}`)
      .then((r) => setFlyer(r.data.flyer))
      .catch((e) => setErr(e?.response?.data?.detail || 'Flyer not available'));
  }, [slug]);

  const download = async () => {
    if (!previewRef.current) return;
    try {
      const canvas = await html2canvas(previewRef.current, { useCORS: true, allowTaint: true, scale: 2 });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url; a.download = `${slug}.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) { alert('Download failed: ' + e.message); }
  };
  const share = () => {
    const cfg = flyer?.config || {};
    const msg = `${cfg.title || 'Flyer'}\n${cfg.subtitle || ''}\n\n${cfg.description || ''}\n\n📞 ${cfg.mobile || ''}\n🔗 ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (err) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6 text-center">
      <div><div className="text-4xl mb-2">🔒</div><div className="text-lg font-black">{err}</div></div>
    </div>
  );
  if (!flyer) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-orange-300">Loading...</div>;

  const cfg = flyer.config || {};
  const imgs = cfg.images || [];
  const gridClass = cfg.image_layout === 'grid_4' ? 'grid-cols-2 grid-rows-2'
                  : cfg.image_layout === 'grid_2' ? 'grid-cols-2 grid-rows-1'
                  : 'grid-cols-1 grid-rows-1';
  // Aspect from format code
  const aspects = { a4: '3/4', wa_story: '9/16', wa_square: '1/1', insta_square: '1/1', insta_story: '9/16', fb_post: '16/9', yt_thumb: '16/9' };
  const aspect = aspects[flyer.format_code] || '3/4';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 p-4 flex flex-col items-center gap-3 text-white" data-testid="flyer-public-view">
      <div className="text-xs opacity-70 mt-4">Powered by Anant Web Portal</div>
      <div className="w-full max-w-md rounded-2xl overflow-hidden border-2 border-orange-400/40 shadow-2xl">
        <div ref={previewRef} className="relative flex flex-col overflow-hidden text-center p-4" style={{ background: cfg.gradient || 'linear-gradient(135deg,#f59e0b,#dc2626)', aspectRatio: aspect, fontFamily: cfg.font_family || 'Poppins' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
          <div className="relative z-10 flex flex-col h-full">
            <div className="text-[8px] uppercase tracking-widest font-black" style={{ color: cfg.accent || '#fef3c7' }}>{cfg.shop_name}</div>
            <div className="text-2xl sm:text-3xl font-black leading-tight mt-2" style={{ color: cfg.title_color || '#fbbf24', textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>{cfg.title}</div>
            <div className="text-sm mt-1 font-bold" style={{ color: cfg.accent || '#fef3c7' }}>{cfg.subtitle}</div>
            {imgs.length > 0 && (
              <div className={`grid ${gridClass} gap-1 flex-1 my-3 min-h-0`}>
                {imgs.slice(0, cfg.image_layout === 'single' ? 1 : cfg.image_layout === 'grid_2' ? 2 : 4).map((u, i) => (
                  <img key={i} src={u} alt="" crossOrigin="anonymous" className="w-full h-full object-cover rounded-lg border-2" style={{ borderColor: cfg.accent || '#fef3c7' }} onError={(e) => { e.target.style.display = 'none'; }} />
                ))}
              </div>
            )}
            {imgs.length === 0 && <div className="flex-1" />}
            <div className="text-xs px-2" style={{ color: cfg.text_color || '#fff' }}>{cfg.description}</div>
            <div className="mt-3 pt-2 border-t-2 text-[10px] space-y-0.5" style={{ borderColor: (cfg.accent || '#fef3c7') + '80', color: cfg.accent || '#fef3c7' }}>
              {cfg.mobile && <div>📞 {cfg.mobile}{cfg.whatsapp && cfg.whatsapp !== cfg.mobile ? ` · 💬 ${cfg.whatsapp}` : ''}</div>}
              {cfg.website && <div>🌐 {cfg.website}</div>}
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 w-full max-w-md">
        <button onClick={download} data-testid="flyer-public-download" className="py-2.5 rounded-lg bg-emerald-500 text-slate-900 font-black text-sm">📥 Download PNG</button>
        <button onClick={share} data-testid="flyer-public-share" className="py-2.5 rounded-lg bg-green-600 text-white font-black text-sm">💬 Share WhatsApp</button>
      </div>
      {cfg.mobile && (
        <a href={`https://wa.me/${String(cfg.whatsapp || cfg.mobile).replace(/[^\d]/g, '')}?text=Hi%2C%20I%20saw%20your%20flyer`}
           target="_blank" rel="noreferrer"
           className="w-full max-w-md py-2.5 rounded-lg bg-orange-500 text-slate-900 font-black text-center text-sm">
          📞 Contact via WhatsApp
        </a>
      )}
      <div className="text-[10px] opacity-60 mt-4">Views: {flyer.public_views || 0} · Anant Smart Flyer Services</div>
    </div>
  );
}
